
import { defineComponent, onMounted, reactive } from 'vue'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'

// @module components
import RouteSheetFilters from '@/modules/NewDispatcher/components/RouteSheetFilters/index.vue'
import RouteSheetRow from '@/modules/NewDispatcher/components/RouteSheetRow/index.vue'

// @types
import { GetRouteSheetParams, RouteSheet } from '@/new-services/types/route-sheet'

// @new-services
import routeSheetAPI from '@/new-services/route-sheet.service'

// @store
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'

const TABLE_COLUMNS = [
    {
        key: 'number',
        name: '№',
    },
    {
        key: 'date',
        name: 'Дата',
    },
    {
        key: 'courier',
        name: 'Курьер',
    },
    {
        key: 'city',
        name: 'Город',
    },
    {
        key: 'sectors',
        name: 'Секторы',
    },
    {
        key: 'waves',
        name: 'Волны',
    },
    {
        key: 'invoicesCount',
        name: 'Кол-во накладных',
    },
    {
        key: 'placesCount',
        name: 'Кол‑во мест',
    },
    {
        key: 'weightSum',
        name: 'Физ вес',
    },
    {
        key: 'volumeWeightSum',
        name: 'Объемный вес',
    },
    {
        key: 'status',
        name: 'Статус',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        RouteSheetFilters,
        RouteSheetRow,
        NewLimit,
        NewPagination,
        FailedFetch,
        NewTable,
    },
    setup() {
        const new_dispatcher_store = useNewDispatcherStore()

        const state = reactive({
            list: [] as RouteSheet[],
            is_loading: false,
            is_failed: false,
            filters: {
                limit: 50,
                current_page: 1,
                total_pages: 1,
                total_items: 1,
                //
                fromDate: null as string | null,
                toDate: null as string | null,
                courierId: null as number | null,
                invoiceNumber: null as string | null,
                number: null as string | null,
                sectorId: null as number | null,
                waveId: null as number | null,
            },
        })

        const loadRouteSheet = async (dispatcher_sector_ids: number[]) => {
            try {
                state.is_loading = true

                const params: GetRouteSheetParams = {
                    page: state.filters.current_page,
                    limit: state.filters.limit,
                    //
                    fromDate: state.filters.fromDate,
                    toDate: state.filters.toDate,
                    courierId: state.filters.courierId,
                    invoiceNumber: state.filters.invoiceNumber,
                    number: state.filters.number,
                    sectorId: state.filters.sectorId,
                    waveId: state.filters.waveId,
                    //
                    dispatcherSectorIds: dispatcher_sector_ids,
                }

                const response = await routeSheetAPI.getRouteSheet(params)
                state.list = response.data.data
                state.filters.total_pages = response.data.meta.last_page
                state.filters.total_items = response.data.meta.total

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const onFiltersChanged = (filters: GetRouteSheetParams) => {
            state.filters.fromDate = filters.fromDate as string | null
            state.filters.toDate = filters.toDate as string | null
            state.filters.courierId = filters.courierId as number | null
            state.filters.invoiceNumber = filters.invoiceNumber as string | null
            state.filters.number = filters.number as string | null
            state.filters.sectorId = filters.sectorId as number | null
            state.filters.waveId = filters.waveId as number | null

            state.filters.current_page = 1

            loadData()
        }

        const onFiltersReset = () => {
            state.filters.fromDate = null
            state.filters.toDate = null
            state.filters.courierId = null
            state.filters.invoiceNumber = null
            state.filters.number = null
            state.filters.sectorId = null
            state.filters.waveId = null

            state.filters.current_page = 1

            loadData()
        }

        const reset = () => {
            state.list = []

            state.filters.total_pages = 1
            state.filters.total_items = 20
            state.filters.current_page = 1

            state.filters.courierId = null
            state.filters.sectorId = null
            state.filters.waveId = null
        }

        const loadData = () => {
            new_dispatcher_store.validation$.$touch()
            if (new_dispatcher_store.validation$.$invalid) {
                reset()
                return
            }

            loadRouteSheet(new_dispatcher_store.dispatcherSectorIds)
        }

        onMounted(loadData)

        const onLimitChange = (new_limit: number) => {
            state.filters.limit = new_limit
            loadData()
        }

        const onChangePage = (new_page: number) => {
            state.filters.current_page = new_page
            loadData()
        }

        return {
            TABLE_COLUMNS,
            //
            new_dispatcher_store,
            state,
            onFiltersChanged,
            onFiltersReset,
            onLimitChange,
            onChangePage,
            loadData,
        }
    },
})
